import * as am4core from "@amcharts/amcharts4/core";
import { ApolloProvider } from "@apollo/client";
import ReactDOM from "react-dom/client";
import App from "./App";
import "./index.css";
import { initializeAnalytics } from "./utils/analytics";
import { startGuides } from "./utils/guides";
import { setSentryUser } from "./integrations/sentry/sentryInit";
import { ThemeProvider } from "./styles/mui/ThemeProvider";
import { GraphQLClient } from "./utils/apollo";
import LaunchDarklyProvider from "./contexts/LaunchDarkly/LaunchDarklyContext";
import { isAuthenticated, loginMkAuth } from "./utils/auth";
import { axiosInstance } from "./utils/axios";
import { isShareRoute } from "./utils/isShareRoute";
import env from "./utils/env";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import {
  endImpersonation,
  isImpersonating,
  isImpersonatorSuperKudu,
} from "./utils/impersonation";
import {
  CLAIMS_LOCAL_STORAGE_KEY,
  ACCESS_TOKEN_STORAGE_KEY,
  EXPIRES_AT_LOCAL_STORAGE_KEY,
  PROFILE_LOCAL_STORAGE_KEY,
  OKTA_GROUPS_LOCAL_STORAGE_KEY,
  SPOG_TOGGLE_LOCAL_STORAGE_KEY,
  POST_LOGIN_REDIRECT_SESSION_KEY,
} from "./const";

if (env.AMCHARTS_LICENSE_CHARTS) {
  am4core.addLicense(env.AMCHARTS_LICENSE_CHARTS);
}

const apolloClient = GraphQLClient();
const apiInstance = axiosInstance;
const isAuthCallbackRoute = () => {
  const path = window.location?.pathname;
  return path === "/auth/callback";
};

export const router = createBrowserRouter(
  [
    {
      path: "*",
      element: <App />,
    },
  ],
  {}
);

const authenticate = async (onFinish: (redirect?: string) => void) => {
  const params = new URLSearchParams(window.location.search);
  const code = params.get("code");
  const state = params.get("state");
  const storedState = sessionStorage.getItem("oauth_state");

  if (code && state) {
    // Verify state to prevent CSRF attacks
    if (state !== storedState) {
      console.error("State mismatch - possible CSRF attack");
      loginMkAuth();
      return;
    }

    // Clear the stored state
    sessionStorage.removeItem("oauth_state");

    try {
      const redirectUri = `${window.location.origin}/auth/callback`;

      const res = await apiInstance.post("/auth/oauth/exchange", {
        code,
        state,
        redirectUri,
      });

      const {
        data: {
          claims,
          accessToken,
          refreshToken,
          expiresAt,
          profile,
          oktaGroups,
        },
      } = res;

      // A fresh login should never keep a previous impersonation session around
      localStorage.removeItem("impersonation_state");
      localStorage.removeItem("original_tokens");

      localStorage.setItem(CLAIMS_LOCAL_STORAGE_KEY, JSON.stringify(claims));
      localStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, accessToken);
      localStorage.setItem("refresh_token", refreshToken);
      localStorage.setItem(EXPIRES_AT_LOCAL_STORAGE_KEY, expiresAt?.toString());
      localStorage.setItem(PROFILE_LOCAL_STORAGE_KEY, JSON.stringify(profile));
      localStorage.setItem(
        OKTA_GROUPS_LOCAL_STORAGE_KEY,
        JSON.stringify(oktaGroups)
      );
      localStorage.setItem(SPOG_TOGGLE_LOCAL_STORAGE_KEY, JSON.stringify(true));
      setSentryUser(profile);

      const postLoginRedirect = sessionStorage.getItem(
        POST_LOGIN_REDIRECT_SESSION_KEY
      );
      sessionStorage.removeItem(POST_LOGIN_REDIRECT_SESSION_KEY);

      onFinish(postLoginRedirect || undefined);
    } catch (error) {
      console.error("Authentication error:", error);
      // Redirect to login on error
      loginMkAuth();
    }
  }
};

export const render = () => {
  ReactDOM.createRoot(document.getElementById("root")!).render(
    <ApolloProvider client={apolloClient}>
      <LaunchDarklyProvider>
        <ThemeProvider>
          <RouterProvider router={router} />
        </ThemeProvider>
      </LaunchDarklyProvider>
    </ApolloProvider>
  );
};

const init = () => {
  if (isShareRoute()) {
    void import("./pages/Share/renderShare").then((m) => m.renderShare());
    return;
  }

  const baseUrl = "/";

  // Smooth Admin → Market Analyzer flow: if we arrive with an admin deep-link
  // parameter while currently impersonating, restore the original session BEFORE
  // the app renders to avoid a confusing "impersonated flash" + reload.
  try {
    const params = new URLSearchParams(window.location.search);
    const impersonationTenantId = params.get("impersonationTenantId");
    if (
      impersonationTenantId &&
      isImpersonating() &&
      isImpersonatorSuperKudu()
    ) {
      endImpersonation();
    }
  } catch {
    // Best effort only
  }

  if (isAuthenticated()) {
    initializeAnalytics();
    void startGuides();
    render();
  }

  if (!isAuthenticated()) {
    if (isAuthCallbackRoute()) {
      authenticate((redirect) => {
        if (redirect && redirect !== "/auth/callback") {
          window.location.href = redirect;
          return;
        }

        window.location.href = baseUrl;
      });
    } else {
      loginMkAuth();
    }
  }
};

init();
